/**
 * DragHandle Component
 *
 * Built-in drag handle with DragIndicatorIcon.
 * Receives drag listeners from useSortable hook via props spread.
 */

import React, { forwardRef } from 'react';
import { IconButton, Tooltip, alpha, useTheme } from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';

export interface DragHandleProps {
  /**
   * ARIA label for screen readers
   * @default "Drag to reorder"
   */
  'aria-label'?: string;

  /**
   * Whether the handle is disabled
   */
  disabled?: boolean;

  /**
   * Custom styles
   */
  sx?: SxProps<Theme>;

  /**
   * Test ID for E2E testing
   */
  'data-testid'?: string;
}

/**
 * Drag handle component with built-in icon and styling.
 * Forwards ref and spreads drag listeners from dnd-kit.
 */
export const DragHandle = forwardRef<HTMLButtonElement, DragHandleProps>(
  function DragHandle(props, ref) {
    const {
      'aria-label': ariaLabel = 'Drag to reorder',
      'data-testid': testId,
      disabled = false,
      sx,
      ...rest
    } = props;

    const theme = useTheme();
    const tooltipTitle = disabled ? '' : ariaLabel;

    // Cast rest props to any to allow dnd-kit listeners to pass through
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listeners = rest as any;

    return (
      <Tooltip title={tooltipTitle} placement="top">
        <span>
          <IconButton
            ref={ref}
            size="small"
            disabled={disabled}
            aria-label={ariaLabel}
            data-testid={testId}
            onClick={(e) => e.stopPropagation()}
            {...listeners}
            sx={{
              cursor: disabled ? 'not-allowed' : 'grab',
              '&:active': {
                cursor: disabled ? 'not-allowed' : 'grabbing',
              },
              '&:hover:not(:disabled)': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '&.Mui-disabled': {
                opacity: 0.4,
              },
              p: 0.5,
              transition: theme.transitions.create(['background-color'], {
                duration: theme.transitions.duration.shortest,
              }),
              ...sx,
            }}
          >
            <DragIndicatorIcon
              fontSize="medium"
              sx={{ color: theme.palette.text.secondary }}
            />
          </IconButton>
        </span>
      </Tooltip>
    );
  }
);

DragHandle.displayName = 'DragHandle';
